import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, Briefcase, ArrowRight, Phone, Heart } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";
import { COMPANY } from "@/lib/constants";

export default function Careers() {
  useSEO({
    title: "Careers | Join the Esprit Home Healthcare Team — Northern Virginia",
    description: "Explore nursing, therapy, and caregiver job openings at Esprit Home Healthcare across Northern Virginia. Apply today.",
  });

  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div>
      <PageHeader
        overline="Careers"
        title="Build a Career of Compassion"
        subtitle="Join a team that brings rehab-quality care into the homes of patients across Northern Virginia. We're hiring nurses, therapists, and caregivers who care."
      />

      <section className="container-page py-16 lg:py-24">
        <div className="grid lg:grid-cols-3 gap-6 mb-14">
          {[
            { icon: Heart, t: "Meaningful Work", d: "Improve quality of life for patients and their families every single day." },
            { icon: Briefcase, t: "Flexible Schedules", d: "Full-time, part-time, and PRN opportunities to fit your life." },
            { icon: MapPin, t: "Close to Home", d: "Serve patients in the Northern Virginia communities you know." },
          ].map((b, i) => (
            <Reveal key={b.t} delay={i * 0.08}>
              <div className="bg-card rounded-2xl border border-border p-7 h-full">
                <span className="grid place-items-center w-12 h-12 rounded-xl bg-secondary/15 text-secondary"><b.icon className="w-6 h-6" /></span>
                <h3 className="font-heading text-2xl text-primary mt-4">{b.t}</h3>
                <p className="text-muted-foreground mt-1">{b.d}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <h2 className="font-heading text-4xl text-primary mb-8">Open Positions</h2>

        {loading ? (
          <p className="text-muted-foreground text-lg">Loading openings…</p>
        ) : jobs.length === 0 ? (
          <div className="bg-[hsl(var(--muted))] rounded-2xl p-10 text-center">
            <p className="text-lg text-muted-foreground">No open positions right now. We'd still love to hear from you — call {COMPANY.phone}.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {jobs.map((job, i) => (
              <Reveal key={job.id} delay={i * 0.05}>
                <Link to={`/careers/${job.slug}`} data-testid={`job-card-${i}`}
                  className="group flex flex-col md:flex-row md:items-center justify-between gap-4 bg-card rounded-2xl border border-border p-6 hover:border-primary hover:shadow-md transition-all">
                  <div>
                    <h3 className="font-heading text-2xl text-primary">{job.title}</h3>
                    <p className="text-muted-foreground mt-1">{job.summary}</p>
                    <div className="flex flex-wrap gap-3 mt-3 text-sm">
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1"><MapPin className="w-4 h-4" /> {job.location}</span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1"><Briefcase className="w-4 h-4" /> {job.employment_type}</span>
                      {job.department && <span className="rounded-full bg-secondary/15 text-secondary px-3 py-1 font-medium">{job.department}</span>}
                    </div>
                  </div>
                  <span className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground h-12 px-6 font-semibold shrink-0 group-hover:gap-3 transition-all">
                    View & Apply <ArrowRight className="w-5 h-5" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        )}

        <div className="mt-12 text-center">
          <a href={COMPANY.phoneHref} className="inline-flex items-center gap-2 text-primary font-semibold text-lg">
            <Phone className="w-5 h-5" /> Questions about a role? Call {COMPANY.phone}
          </a>
        </div>
      </section>
    </div>
  );
}
